import { fetchOpenAIText } from "../utils/openaiClient";

export function useOpenQuestionsGeneratorLogic({
  openQuestionsInput, setOpenQuestionsInput, openQuestionsLevel, openQuestionsCount,
  setOpenQuestionsLoading, generatedOpenQuestions, setGeneratedOpenQuestions,
  currentTab, onInsertToBoard, activeEditor,
  setShowOpenQuestionsGenerator, setSelectedTemplate,
}) {
  const handleOpenQuestionsGenerate = async () => {
    if (!openQuestionsInput.trim()) return;
    setOpenQuestionsLoading(true);
    try {
      const levelComplexity = { A1: 'very simple, concrete questions, 5–12 words', A2: 'simple questions, 8–15 words', B1: 'intermediate questions, 8–18 words', B2: 'complex questions, 10–22 words', C1: 'advanced questions, 12–26 words', C2: 'highly advanced questions, 14–30 words' };
      const buildPrompt = (count) => `Create open-ended questions for the following text. Generate exactly ${count} content-based open questions that encourage critical thinking and discussion.

CEFR level: ${openQuestionsLevel} (${levelComplexity[openQuestionsLevel] || ''}).
Rules: No yes/no questions. One idea per question. Reference core ideas of the text.

IMPORTANT: Return ONLY valid JSON:
{
  "title": "Open Questions Exercise",
  "level": "${openQuestionsLevel}",
  "questions": [
    { "question": "Question text here?", "suggestedAnswer": "Suggested answer or discussion points here" }
  ]
}

Text: ${openQuestionsInput}`;

      const runOnce = async (count) => {
        const raw = await fetchOpenAIText({ prompt: buildPrompt(count), temperature: 0.7, maxTokens: 1200 });
        if (!raw) return null;
        const content = raw.trim().replace(/^```json[\r\n]+/, '').replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
        try {
          return JSON.parse(content);
        } catch (_) {
          return null;
        }
      };

      let jsonData = await runOnce(openQuestionsCount);
      // Retry once for whatever's missing rather than padding with
      // duplicates or silently accepting an under-delivered set.
      if (jsonData && Array.isArray(jsonData.questions) && jsonData.questions.length < openQuestionsCount) {
        const missing = openQuestionsCount - jsonData.questions.length;
        const fix = await runOnce(missing);
        if (fix && Array.isArray(fix.questions)) {
          jsonData = { ...jsonData, questions: [...jsonData.questions, ...fix.questions] };
        }
      }

      if (!jsonData || !Array.isArray(jsonData.questions) || jsonData.questions.length === 0) {
        throw new Error('Could not generate the exercise — the AI response was invalid. Please try again.');
      }
      // Carry the source text along as referenceText so the exercise's
      // built-in AI review can ground-check student answers against it —
      // without this, the grounding feature has nothing to check against.
      setGeneratedOpenQuestions({ ...jsonData, referenceText: openQuestionsInput });
    } catch (e) {
      alert(e?.message || 'Could not generate the exercise. Please try again.');
    } finally {
      setOpenQuestionsLoading(false);
    }
  };

  const handleOpenQuestionsInsert = () => {
    if (!generatedOpenQuestions) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedOpenQuestions, 'openquestions');
      setShowOpenQuestionsGenerator(false);
      setOpenQuestionsInput('');
      setGeneratedOpenQuestions(null);
      setSelectedTemplate(null);
      return;
    }
    if (activeEditor) {
      activeEditor.chain().focus().insertOpenQuestionsExercise({
        questions: generatedOpenQuestions.questions,
        title: generatedOpenQuestions.title,
        level: generatedOpenQuestions.level,
        referenceText: generatedOpenQuestions.referenceText,
      }).run();
    }
    setShowOpenQuestionsGenerator(false);
    setOpenQuestionsInput('');
    setGeneratedOpenQuestions(null);
    setSelectedTemplate(null);
  };

  const handleOpenQuestionsCancel = () => {
    setShowOpenQuestionsGenerator(false);
    setOpenQuestionsInput('');
    setGeneratedOpenQuestions(null);
    setSelectedTemplate(null);
  };

  return { handleOpenQuestionsGenerate, handleOpenQuestionsInsert, handleOpenQuestionsCancel };
}
